import { AlertTriangle, HelpCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Dialog, ModalContent, ModalFooter } from '@/components/ui/modal';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  ConfirmContext,
  ConfirmFn,
  ConfirmOptions,
  hasImperativeConfirm,
  registerImperativeConfirm,
} from './confirm-context';

/**
 * Mounts a single styled confirm dialog for the whole app. Pair with
 * `useConfirm()` / `confirmImperative()` from `confirm-context.ts`:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete this?', variant: 'destructive' }))) return;
 *
 * The dialog reuses the house modal shell (`ModalContent`/`ModalFooter`) so it
 * inherits the overlay, focus trap, Esc-to-close and theming. Cancel is the
 * first focusable element, so a stray Enter never triggers a destructive action.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  // `options` lingers through the close animation so the text never blanks out.
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const resolverRef = React.useRef<((value: boolean) => void) | null>(null);

  const confirm = React.useCallback<ConfirmFn>(
    nextOptions =>
      new Promise<boolean>(resolve => {
        resolverRef.current = resolve;
        setOptions(nextOptions);
        setInputValue('');
        setOpen(true);
      }),
    []
  );

  React.useEffect(() => {
    registerImperativeConfirm(confirm);
    return () => {
      if (hasImperativeConfirm(confirm)) registerImperativeConfirm(null);
    };
  }, [confirm]);

  const settle = React.useCallback((result: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    resolve?.(result);
  }, []);

  const variant = options?.variant ?? 'default';
  const Icon = variant === 'destructive' ? AlertTriangle : HelpCircle;
  const input = options?.input;
  const inputMissing = !!input?.required && !inputValue.trim();

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={open}
        onOpenChange={next => {
          if (!next) settle(false);
        }}
      >
        <ModalContent size="sm" className="gap-0">
          <div className="flex items-start gap-4 px-6 py-6">
            <span
              aria-hidden
              className={cn(
                'mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-full',
                variant === 'destructive' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg font-semibold leading-tight tracking-[-0.01em]">
                {options?.title ?? ''}
              </DialogTitle>
              {options?.description ? (
                <DialogDescription className="mt-2 text-sm leading-6 text-muted-foreground">
                  {options.description}
                </DialogDescription>
              ) : null}
              {input ? (
                <div className="mt-4 space-y-1.5">
                  <Label htmlFor="confirm-dialog-input">{input.label}</Label>
                  <Textarea
                    id="confirm-dialog-input"
                    rows={3}
                    value={inputValue}
                    placeholder={input.placeholder}
                    onChange={event => {
                      setInputValue(event.target.value);
                      input.onChange(event.target.value);
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <ModalFooter
            onCancel={() => settle(false)}
            cancelLabel={options?.cancelLabel ?? t('common.cancel')}
            submitLabel={options?.confirmLabel ?? t('common.confirm')}
            submitVariant={variant}
            submitDisabled={inputMissing}
            onSubmit={() => settle(true)}
          />
        </ModalContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
